/**
 * @module app.um
 */

import { autoinject } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { deepClone } from 'app/shared/lib/utilities';
import { localSort } from 'app/shared/lib/sorting';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthService } from 'app/um/services/auth-service';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class ServiceCorrespondenceGrid extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object protected services: UM.AuthService[] = [];
    @bindable.number protected authId: number | undefined;

    protected selectedServices: UM.AuthService[] = [];
    protected sortedServices: UM.AuthService[] = [];
    private localSorting = new Sorting();

    protected $servicesTable!: EssTableCustomElement<UM.AuthService>;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly authService: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.services) return;
        this.reset();
    }


    ////****************** Events ***************************************************////

    protected selectChanged({ detail }: SelectEvent<UM.AuthService>): void {
        this.selectedServices.splice(0);
        //shallowclone for less API footprint
        detail.items.forEach(s => this.selectedServices.push({ id: s.id } as UM.AuthService));
    }


    ////****************** Functions ************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { localSorting } = this;
        if(direction === null) {
            localSorting.remove(property);
        } else {
            localSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(localSorting.entries.length > 0) {
            localSort(this.sortedServices, localSorting);
        }
        else {
            const activeServiceSorting = new Sorting();
            activeServiceSorting.add({
                reference: 'correspondenceRulesTimestamp',
                direction: SortDirection.Ascending //not run or oldest-run, up top.
            });
            localSort(this.sortedServices, activeServiceSorting);
        }
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        return this.selectedServices.length > 0; //doesn't really matter
    }

    public async submit(): Promise<boolean> {
        return await this.triggerCorrespondenceRules();
    }

    public reset(): void {
        //on ess-form reset; this.services should be bound now.
        this.sortedServices = deepClone(this.services); //assume filtering was done by what calls this
        this.selectedServices.splice(0);
        this.$servicesTable.deselectAll();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async triggerCorrespondenceRules(): Promise<boolean> {
        if(!this.authId) return false;

        let isSuccessful = false;
        try {
            const response = await this.authService.triggerDetailCorrespondence(this.authId.toString(), this.selectedServices);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:correspondenceRules.$', isSuccessful);
            return isSuccessful; //on isSuccessful, the owning page is responsible for refreshing its own grids etc
        }
    }
}

